import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, RefreshCcw, ScanSearch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { editorialReviewApi } from '@/lib/api';
import { ReviewFilters } from './editorial-review/ReviewFilters';
import { ReviewQueueList } from './editorial-review/ReviewQueueList';
import { ReviewWorkspace } from './editorial-review/ReviewWorkspace';

const EMPTY_METRICS = { total: 0, open: 0, critical: 0, high: 0, unassigned: 0, ai_pending: 0 };

export const EditorialReviewInboxPanel = () => {
  const [filters, setFilters] = useState({ status: 'all', risk: 'all', assignee: 'all', search: '' });
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [editors, setEditors] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const result = await editorialReviewApi.list(filters);
      setRows(result.rows || []);
      setMetrics(result.metrics || EMPTY_METRICS);
      setSelectedId((current) => current || result.rows?.[0]?.id || '');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not load the editorial queue.');
    } finally { setLoading(false); }
  }, [filters]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) { setDetail(null); return; }
    try { setDetail(await editorialReviewApi.detail(selectedId)); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Could not load this review.'); }
  }, [selectedId]);

  useEffect(() => {
    editorialReviewApi.editors().then((result) => setEditors(result.editors || [])).catch(() => setEditors([]));
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(loadQueue, filters.search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [loadQueue, filters.search]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    if (!metrics.ai_pending) return undefined;
    const timer = window.setInterval(() => { loadQueue(); loadDetail(); }, 3000);
    return () => window.clearInterval(timer);
  }, [metrics.ai_pending, loadQueue, loadDetail]);

  const mutate = async (operation, success) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const review = await operation();
      setDetail((current) => ({ ...(current || {}), review }));
      toast.success(success);
      await loadQueue();
      await loadDetail();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Review update failed.');
    } finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const result = await editorialReviewApi.sync({ include_ai: true, force_ai: false });
      toast.success(`${result.created} added · ${result.ai_queued} queued for ambiguity ranking`);
      await loadQueue();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Manuscript sync failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-5" data-testid="editorial-review-inbox-panel">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground"><ClipboardCheck className="h-4 w-4 text-primary" /> Editorial operations</div>
          <h1 className="mt-2 font-serif-novel text-3xl font-semibold" data-testid="editorial-review-inbox-title">Manuscript Review Inbox</h1>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground" data-testid="editorial-review-inbox-summary">Hybrid rules surface the chapters that need context; Jev ranks ambiguous evidence without replacing human judgment.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { loadQueue(); loadDetail(); }} disabled={loading} data-testid="review-refresh-button"><RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>
          <Button onClick={sync} disabled={busy} data-testid="review-sync-button"><ScanSearch className="mr-1.5 h-3.5 w-3.5" /> Sync completed manuscripts</Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[15rem_22rem_minmax(0,1fr)]">
        <ReviewFilters filters={filters} setFilters={setFilters} editors={editors} metrics={metrics} />
        <ReviewQueueList rows={rows} selectedId={selectedId} onSelect={setSelectedId} loading={loading} />
        <ReviewWorkspace
          detail={detail}
          editors={editors}
          busy={busy}
          onAssign={(editor) => mutate(() => editorialReviewApi.assign(selectedId, editor), editor ? `Assigned to ${editor}.` : 'Review unassigned.')}
          onNotes={(notes) => mutate(() => editorialReviewApi.saveNotes(selectedId, notes), 'Review notes saved.')}
          onDecision={(action, notes) => mutate(() => editorialReviewApi.decide(selectedId, action, notes), `Review ${action.replaceAll('_', ' ')}.`)}
          onRescore={() => mutate(() => editorialReviewApi.rescore(selectedId), 'Ambiguity ranking queued.')}
        />
      </div>
    </div>
  );
};

export default EditorialReviewInboxPanel;